import { createApi } from '@reduxjs/toolkit/query/react';
import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { getEncryptParams } from 'src/keycloak';
import { ICreateSharepointResourceParams } from 'src/types/document';
import { getPayload } from 'src/utils/encryption';
import { blobToBuffer, decryptFileData } from 'src/utils/files/file_encryption';
import { DataApiInputParams } from '../types/api';
import analytics from './analytics';
import assistant from './assistant';
import billings from './billings';
import canvas from './canvas';
import common from './common';
import confluence from './confluence';
import googleDrive from './googleDrive';
import { CHAT_MS_BASE_URL, customBaseQuery } from './customBaseQuery';
import docAnalyst from './docAnalyst';
import smartMeet from './smartMeet';
import projects from './projects';
import icon from './icon';
import permissions from './permissions';
import roles from './roles';
import search from './search';
import settings from './settings';
import ContactSupport from './support';
import Tenant from './tenant';
import userGroups from './userGroups';
import users from './users';
import workflow from './workflow';
import {
  appendFormData,
  formatMessagesResponse,
  getHeaders,
  getQueryString,
  serializeErrorResponse
} from './utils';
import WordCreditUsage from './wordCreditUsage';
import model from './modelConfiguration';

export const api: any = createApi({
  baseQuery: customBaseQuery,
  tagTypes: [
    'User',
    'Files',
    'Chats',
    'Messages',
    'Canvas',
    'Tags',
    'ChatParams',
    'Roles',
    'UserRoles',
    'Permissions',
    'Groups',
    'IdentityProvider',
    'ResourceProvider',
    'Favourites',
    'DocAnalyst',
    'SmartMeet',
    'Projects',
    'Assistant',
    'Confluence',
    'GoogleDrive',
    'Icon',
    'ResourceSettings',
    'helpSupport',
    'Tenants',
    'ModelTiersRateLimits',
    'WordCreditUsage',
    'ServiceClient',
    'UserActionSettings',
    'TenantsLogo',
    'Billings',
    'Models',
    'UserPreferences',
    'Workflows',
    'WorkflowWebhook',
    'LicenseLimits',
    'Payg',
    'Notifications',
    'NotificationPreferences',
    'OrganizationNotificationPreferences',
    'Skills',
    'UploadSessions',
    'Share',
    'newAssistant',
    'newAssistantContentAccessRights'
  ],
  endpoints: (builder) => ({
    getDocuments: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `files/file-details?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
        return result.error
          ? result
          : {
              data: {
                ...result.data,
                data: Array.isArray(result?.data.data)
                  ? result?.data?.data.map((item) => ({
                      ...item,
                      attrs: (Array.isArray(item.attrs)
                        ? item.attrs
                        : []
                      ).reduce(
                        (acc, attr) => ({ ...acc, [attr.name]: attr.value }),
                        {}
                      ),
                      tags: Array.isArray(item.tags) ? item.tags : []
                    }))
                  : []
              }
            };
      },
      providesTags: ['Files']
    }),
    uploadDocuments: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        const uploadFormData = params.formData;
        if (uploadFormData instanceof FormData) {
          const formDataEntries: Array<[string, unknown]> = [];
          uploadFormData.forEach((value, key) => {
            formDataEntries.push([
              key,
              value instanceof File
                ? {
                    name: value.name,
                    type: value.type,
                    size: value.size
                  }
                : value
            ]);
          });
        }
        return baseQuery({
          url: `files/file`,
          method: 'POST',
          body: uploadFormData,
          ...getHeaders(`files/file`, uploadFormData)
        });
      }
    }),
    processDocuments: builder.mutation({
      async queryFn({ params }, { getState }, extraOptions, baseQuery) {
        return baseQuery({
          url: `files/file`,
          method: 'PATCH',
          body: params.formData,
          ...getHeaders(`files/file`, params.formData)
        });
      },
      invalidatesTags: ['Tags']
    }),
    updateResource: builder.mutation({
      async queryFn({ params }, { getState }, extraOptions, baseQuery) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/folder`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`files/folder`, formData)
        });
      },
      invalidatesTags: ['Files', 'Favourites']
    }),
    createResource: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams | ICreateSharepointResourceParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/folder`,
          method: 'POST',
          body: formData,
          ...getHeaders(`files/folder`, formData)
        });
      }
    }),
    addFavourite: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `favourites/favourite`,
          method: 'POST',
          body: formData,
          ...getHeaders(`favourites/favourite`, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    getFavourite: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = 'favourites/favourite';
        if (IS_ENCRYPTION_ENABLED) {
          url = `${url}?payload=${btoa(getPayload('', getEncryptParams()))}`;
        }
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Favourites']
    }),
    removeFavourite: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `favourites/favourite`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    getChats: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `${CHAT_MS_BASE_URL}chat?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url, null, true),
          ms: true
        });
      },
      providesTags: ['Chats']
    }),
    createChat: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    deleteChat: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    renameChat: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url: url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    getChatParams: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `expert-advisor/chat-params?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['ChatParams']
    }),
    postChatParams: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `expert-advisor/chat-params`;
        return fetchWithBQ({
          url: url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['ChatParams']
    }),
    getMessages: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `${CHAT_MS_BASE_URL}chat-msg?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url, null, true),
          ms: true
        });
        return result?.error ? result : formatMessagesResponse(result);
      }
    }),
    postMessage: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat-msg`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    patchMessage: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat-msg`;
        return fetchWithBQ({
          url: url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    getChatTags: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `${CHAT_MS_BASE_URL}chat-tags?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      }
    }),
    updateChatTags: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `${CHAT_MS_BASE_URL}chat-tags`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      }
    }),
    getFilesTags: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `files/file-tag?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Tags']
    }),
    deleteDocument: builder.mutation({
      async queryFn(
        args: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(args.params);
        let url = `files/file`;

        return fetchWithBQ({
          url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    postDocTags: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `files/tagged-file`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Tags', 'Files']
    }),
    deleteDocTags: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `files/tagged-file`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Tags', 'Files']
    }),
    downloadFile: builder.query({
      async queryFn(
        args: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `files/file?${getQueryString(args.params)}`;
        const result: any = await fetchWithBQ({
          returnUnparsedResponse: true,
          url,
          ...getHeaders(url),
          responseHandler: async (response) => {
            if (response.status === 404) {
              return {
                extended: {
                  error_name: 'REFERENCE_DOCUMENT_DELETED'
                }
              };
            } else if (response.status === 400) {
              return {
                extended: {
                  error_name: 'INSUFFICIENT_PRIVILEGES'
                }
              };
            }

            return {
              ret_val: await response.blob()
            };
          }
        });
        if (result.error) {
          return serializeErrorResponse(result.error.data);
        }
        const bufferData = (await blobToBuffer(
          result?.data?.ret_val as Blob
        )) as Buffer;
        let decryptedData = await decryptFileData(bufferData);
        decryptedData = {
          blob: decryptedData,
          contentType: result.meta.response.headers.get('Ea-Content-Type'),
          format: result.meta.response.headers.get('Ea-File-Format')
        };
        return decryptedData
          ? { data: decryptedData }
          : serializeErrorResponse(decryptedData.extended);
      }
    }),
    getUserPreferences: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `users/user-settings?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['UserPreferences']
    }),
    getLicenseLimits: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `license/license-limits?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['LicenseLimits']
    }),
    updateUserPreferences: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `users/user-settings`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      }
    }),
    getUserOrUserGroups: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const queryString = getQueryString(arg.params);
        let url = `keycloak/User-Group${
          queryString.length ? '?' + queryString : ''
        }`;

        if (IS_ENCRYPTION_ENABLED) {
          url = `${url}${queryString.length ? '&' : '?'}payload=${btoa(
            getPayload('', getEncryptParams())
          )}`;
        }

        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Permissions']
    }),
    getContentAccessRights: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const queryString = getQueryString(arg.params);
        let url = `files/folder${queryString.length ? '?' + queryString : ''}`;
        if (IS_ENCRYPTION_ENABLED) {
          url = `${url}${queryString.length ? '&' : '?'}payload=${btoa(
            getPayload('', getEncryptParams())
          )}`;
        }

        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
        return result?.error ? result : result?.data;
      }
    }),

    syncProviderResource: builder.mutation({
      async queryFn({ params }, _, __, baseQuery) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/resource-providers`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`files/resource-providers`, formData)
        });
      },
      invalidatesTags: ['Files']
    }),
    shareResource: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `public-resources/`;
        return fetchWithBQ({
          url: url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData)
        });
      }
    }),
    getShareResource: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = `public-resources/?${getQueryString(arg?.params)}`;
        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
        return result?.error ? result : formatMessagesResponse(result);
      }
    }),
    checkFileUploadLimit: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);

        return baseQuery({
          url: `files/files_count`,
          method: 'POST',
          body: formData,
          ...getHeaders(`files/files_count`, formData)
        });
      }
    }),
    getUploadedFilesStatus: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        let url = `files/status?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
        return result.error ? result : result.data;
      }
    }),
    getResourceChatCount: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `expert-advisor/chat_count?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      }
    }),
    getActions: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `mcp/actions?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      }
    }),
    createConnector: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `mcp/connectors`,
          method: 'POST',
          body: formData,
          ...getHeaders(`mcp/connectors`, formData)
        });
      }
    }),
    removeConnector: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `mcp/connectors`,
          method: 'DELETE',
          body: formData,
          ...getHeaders(`mcp/connectors`, formData)
        });
      }
    }),
    reauthorizeConnector: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `mcp/connectors`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`mcp/connectors`, formData)
        });
      }
    }),
    ...users(builder),
    ...canvas(builder),
    ...roles(builder),
    ...userGroups(builder),
    ...permissions(builder),
    ...common(builder),
    ...analytics(builder),
    ...settings(builder),
    ...docAnalyst(builder),
    ...assistant(builder),
    ...confluence(builder),
    ...googleDrive(builder),
    ...icon(builder),
    ...ContactSupport(builder),
    ...Tenant(builder),
    ...WordCreditUsage(builder),
    ...billings(builder),
    ...smartMeet(builder),
    ...model(builder),
    ...workflow(builder),
    ...projects(builder),
    ...search(builder)
  })
});
export const {
  // Common
  useLazyGetLookupsQuery,
  useLazyGetAppSettingsQuery,
  useUpdateAppSettingsMutation,

  //  Tags
  useLazyGetTagsQuery,
  usePostDocTagsMutation,
  useDeleteDocTagsMutation,
  useLazyGetFilesTagsQuery,

  // CHATS
  useLazyGetChatsQuery,
  useCreateChatMutation,
  useDeleteChatMutation,
  useRenameChatMutation,

  // CHAT MESSAGE
  useLazyGetMessagesQuery,
  usePostMessageMutation,
  usePatchMessageMutation,

  // CHAT PARAMS
  useLazyGetChatParamsQuery,
  usePostChatParamsMutation,

  //CHAT TAGS
  useLazyGetChatTagsQuery,
  useUpdateChatTagsMutation,

  //
  useShareResourceMutation,
  useLazyGetShareResourceQuery,
  useGetShareResourceQuery,

  // DOCUMENTS
  useLazyGetDocumentsQuery,
  useUploadDocumentsMutation,
  useLazyDownloadFileQuery,
  useUpdateResourceMutation,
  useCreateResourceMutation,
  useDeleteDocumentMutation,
  useProcessDocumentsMutation,
  useSyncProviderResourceMutation,
  useCheckFileUploadLimitMutation,

  // Favourites
  useAddFavouriteMutation,
  useGetFavouriteQuery,
  useLazyGetFavouriteQuery,
  useRemoveFavouriteMutation,

  // USER SETTINGS
  useLazyGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useLazyGetLicenseLimitsQuery,

  //CHat count
  useLazyGetResourceChatCountQuery,

  // USERS
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUpdateStatusMutation,

  // ROLES
  useLazyGetRolesQuery,
  useGetRolesQuery,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useUpdateRoleMutation,

  // USER GROUPS
  useLazyGetUserGroupsQuery,
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useUpdateUserGroupsMutation,
  useAssignUserRoleToUserGroupMutation,

  // Permissions
  useLazyGetPermissionsQuery,

  // CONTENT SHARING

  useLazyGetUserOrUserGroupsQuery,
  useLazyGetContentAccessRightsQuery,

  // Stats
  useLazyGetTenantAnalyticsQuery,
  useLazyGetChatBigNumbersQuery,
  useLazyGetUsersCountsQuery,
  useLazyGetProcessedDocumentsQuery,
  useLazyGetUsageQuery,
  useLazyGetProfileStatsCountQuery,
  useLazyGetCreditConsumptionDataQuery,
  useGetTenantCreationDateQuery,
  useGetAllTenantsQuery,
  useLazyGetAllTenantsQuery,

  // Settings
  useCreateIdentityProviderMutation,
  useUpdateIdentityProviderMutation,
  useGetResourceProviderQuery,
  useGetIdentityProviderQuery,
  useLazyGetIdentityProviderQuery,
  useDeleteIdentityProviderMutation,
  useCreateResourceProviderMutation,
  useUpdateResourceProviderMutation,
  useDeleteResourceProviderMutation,
  useLazyGetResourceProviderQuery,
  useLazyDownloadCertificateQuery,
  useGetModelsQuery,
  useLazyGetModelsRateLimitTiersQuery,
  useLazyGetConfiguredAndDefaultModelsQuery,

  // Doc Analyst
  useGetAnalysisHistoryQuery,
  useLazyGetAnalysisHistoryQuery,
  useGetAnalysisQuery,
  useLazyGetAnalysisQuery,
  useCreateAnalysisMutation,
  useDeleteAnalysisMutation,
  useGetDatasetsQuery,
  useLazyGetDatasetsQuery,
  useGetDatasetQuery,
  useLazyGetDatasetQuery,
  useCreateDatasetMutation,
  useDeleteDatasetMutation,
  useGetQuestionnairesQuery,
  useLazyGetQuestionnairesQuery,
  useGetQuestionnaireQuery,
  useLazyGetQuestionnaireQuery,
  useCreateQuestionnaireMutation,
  useEditQuestionnaireMutation,
  useDeleteQuestionnaireMutation,
  useLazyGetAnalysisDetailQuery,
  useGetAnalysisDetailQuery,
  useUpdateAnalysisDetailMutation,
  useUpdateAnalysisMutation,
  useGetQuestionnaireSharedUsersQuery,
  useLazyGetQuestionnaireSharedUsersQuery,
  useSendQuestionnaireMutation,

  // Assistant
  useGetAssistantHistoryQuery,
  useLazyGetAssistantHistoryQuery,
  useGetAssistantQuery,
  useLazyGetAssistantQuery,
  useGetAllAssistantsQuery,
  useLazyGetAllAssistantsQuery,
  useLazyGetAssistantDetailQuery,
  useCreateAssistantMutation,
  useDeleteAssistantMutation,
  useUpdateAssistantMutation,
  useUpdateAssistantResourceMutation,
  useLazyGetAssistantContentAccessRightsQuery,

  // Confluence
  useGetConfluenceContentQuery,
  useLazyGetConfluenceContentQuery,

  // GoogleDrive
  useGetGoogleDriveContentQuery,
  useLazyGetGoogleDriveContentQuery,

  // Icons
  useGetIconsQuery,
  useLazyGetIconsQuery,
  useUploadIconMutation,
  useDeleteIconMutation,

  // Search
  useLazyGetHybridSearchQuery,
  useGetRecentFilesQuery,
  // Polling on Reconnect Azure PubSub
  useLazyGetUploadedFilesStatusQuery,

  // Help&Support
  useCreateHelpSupportFeedbackMutation,

  // Tenant Management
  useLazyGetTenantsQuery,
  useReprocessTenantFilesMutation,
  useUpdateTenantPlanMutation,
  useUpdateOrgLogoMutation,
  useLazyGetOrgLogoQuery,
  useDeleteLogoMutation,
  useCreateTenantMutation,
  useDeleteTenantMutation,
  useCreateServiceClientMutation,
  useUpdateServiceClientMutation,
  useLazyGetServiceClientQuery,
  useResendTenantUserCreationEmailMutation,
  useCreateCompanyInfoMutation,

  // voice to text
  useUpdateWordCreditUsageMutation,

  // Canvas
  useUpdateCanvasContentMutation,
  useLazyGetCanvasByRevisionQuery,
  useRestoreRevisionMutation,

  // MCP
  useGetActionsQuery,
  useLazyGetActionsQuery,
  useCreateConnectorMutation,
  useRemoveConnectorMutation,
  useReauthorizeConnectorMutation,

  // Billing
  useLazyGetAddCardUrlQuery,
  useLazyGetDashBoardUrlQuery,
  useLazyGetBillingPreviewQuery,
  useLazyGetProductPricesQuery,
  usePostSubscriptionMutation,
  useLazyGetBillingInfoQuery,
  useLazyGetAddOnsQuery,
  usePostAddOnMutation,
  useDeleteAddOnMutation,

  //Models
  useLazyFetchModelsQuery,
  useCreateModelMutation,
  useUpdateModelMutation,
  useDeprecateModelMutation,
  useDeleteModelMutation,
  useUpdateModelPropertiesMutation,

  useLazyGetRenewSubscriptionQuery,

  // Smart Meet
  useLazyGetExistingMeetingsQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
  useLazyGetSmartMeetAccessRightsQuery,
  useGetSmartMeetAccessRightsQuery,
  useUpdateSmartMeetResourceMutation,

  useCreateWorkflowMutation,
  useGetWorkflowQuery,
  useLazyGetWorkflowQuery,
  useRunWorkflowMutation,
  useUpdateWorkflowMutation,
  usePatchWorkflowMutation,
  useDuplicateWorkflowMutation,
  useDeleteWorkflowMutation,
  useGetWorkflowRunsQuery,
  useLazyGetWorkflowRunDetailQuery,
  useGetWorkflowVersionsQuery,
  useGetWorkflowsPricingStatsQuery,
  useGetWorkflowUsageQuery,
  useGetWorkflowTriggerQuery,
  useGetWorkflowWebhookQuery,
  useUpdateWorkflowWebhookMutation,
  useConnectOutlookMutation,
  // Projects
  useGetExistingProjectsQuery,
  useLazyGetExistingProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useLazyGetProjectAccessRightsQuery,
  useUpdateProjectResourceMutation
} = api;
